import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Manager } from 'react-popper';

import { apiBuilder } from 'utils/api/common-requests/common-requests';
import * as endpoints from 'utils/api/endpoints';
import * as expressions from 'utils/helpers/expression-utils';

import { useSearchInputField } from '../../hooks/use-search-input-field';
import { useOutsideClick } from 'utils/hooks/use-outside-click';
import ResultWrapper from '../search-components/result-wrapper';
import { INPUT_TIMEOUT, MIN_INPUT_LENGTH } from '../top-search-panel/constants';

import { InputField } from '../search-components/input-field';

import {
    SearchPanelFullWrapper,
    DataWrap,
    Row,
    ControlsTab,
    PlayerLink,
    ContentNameItems
} from './user-search-panel.styled';

const searchAPIOrders = [{ property: 'id', direction: 'DESC' }];
const PAGE = 0;
const SIZE = 1000;

const UserSearchPanel = ({ isOnlyRecentPlayer = false, onChange, placeholder }) => {
    const [isSearchDropDownOpen, setIsSearchDropDownOpen] = useState(false);
    const [playerSearchResults, setPlayerSearchResults] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [debouncedValue, setDebouncedValue] = useState('');

    const { inputValue, handleReset, handleInputChange } = useSearchInputField();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(inputValue);
        }, INPUT_TIMEOUT);

        return () => {
            clearTimeout(timer);
        };
    }, [inputValue]);

    const shouldSearch = useMemo(() => {
        return isSearchDropDownOpen && inputValue?.length >= MIN_INPUT_LENGTH && debouncedValue === inputValue;
    }, [isSearchDropDownOpen, inputValue, debouncedValue]);

    useEffect(() => {
        if (!shouldSearch) {
            return;
        }

        setProcessing(true);

        apiBuilder()
            .post(endpoints.usersSearch, {
                size: SIZE,
                page: PAGE,
                orders: searchAPIOrders,
                expression: expressions.like('userName', expressions.string(`%${inputValue}%`))
            })
            .then(resp => {
                setPlayerSearchResults(resp?.data?.content);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [inputValue, shouldSearch, isOnlyRecentPlayer]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review hook dependencies (baseline when the rule was enabled)
    const handleOpenSearchDropDown = () => setIsSearchDropDownOpen(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review hook dependencies (baseline when the rule was enabled)
    const handleCloseSearchDropDown = () => setIsSearchDropDownOpen(false);

    const dropdownRef = useRef(null);
    const containerPanelRef = useRef(null);
    useOutsideClick({
        ref: [containerPanelRef, dropdownRef],
        callback: handleCloseSearchDropDown,
        isEnabled: isSearchDropDownOpen
    });

    const handleSearchInput = useCallback(
        e => {
            handleInputChange(e);
            if (!isSearchDropDownOpen) {
                handleOpenSearchDropDown();
            }
        },
        [handleInputChange, isSearchDropDownOpen, handleOpenSearchDropDown]
    );

    const handleCancelSearch = useCallback(() => {
        handleReset?.();
        handleCloseSearchDropDown?.();
    }, [handleReset]);

    const handleOnSearchResultClick = useCallback(
        (e, user) => {
            handleInputChange({ ...e, target: { value: user.id + '. ' + user.username } });
            onChange?.(user.id);
            handleCloseSearchDropDown();
        },
        [handleInputChange, onChange, handleCloseSearchDropDown]
    );

    return (
        <Manager>
            <SearchPanelFullWrapper ref={containerPanelRef}>
                <InputField
                    value={inputValue}
                    onClick={handleOpenSearchDropDown}
                    onChange={handleSearchInput}
                    onCancel={handleCancelSearch}
                    processing={processing}
                    placeholder={placeholder}
                    isSearchPanel
                />

                {!!isSearchDropDownOpen && (
                    <DataWrap isSearchPanel ref={dropdownRef}>
                        <ResultWrapper processing={processing} isNoData={!playerSearchResults?.length}>
                            {playerSearchResults?.map(user => {
                                const { username, id } = user;

                                return (
                                    <Row key={id}>
                                        <ControlsTab>
                                            <ContentNameItems onClick={e => handleOnSearchResultClick(e, user)}>
                                                <PlayerLink>
                                                    {id}. {username}
                                                </PlayerLink>
                                            </ContentNameItems>
                                        </ControlsTab>
                                    </Row>
                                );
                            })}
                        </ResultWrapper>
                    </DataWrap>
                )}
            </SearchPanelFullWrapper>
        </Manager>
    );
};

export default UserSearchPanel;
